import React, { useCallback, useEffect } from "react";
import { useMst } from "../../../stores/Root";
import { QRCode, Skeleton, Typography } from "antd";
import { BlingsButton } from "../../../components/BlingsButton/BlingsButton";
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
} from "aws-amplify/auth";
import { getENV } from "../../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IUserModel } from "../../../stores/userModel";

const { Title, Text, Paragraph } = Typography;

// This is your HOC
const withSetupTOTPMFA = (WrappedComponent: React.ComponentType<any>) => {
  return (props: any) => {
    // State declaration
    const { userStore }: { userStore: IUserModel } = useMst((store) => {
      return {
        userStore: store.userStore,
      };
    });
    const [needToSetTOTP, setNeedToSetTOTP] = React.useState<boolean>(false);

    const [QRCodeValue, setQRCodeValue] = React.useState<string | null>(null);
    const [confirmationCount, setConfirmationCount] = React.useState<number>(0);

    // Function Declaration
    const setupTOTP = useCallback(async () => {
      try {
        const userEmail = userStore.email;
        const appName = getENV() === "master" ? "Blings" : "Blings: dev";
        const totpSetupDetails = await setUpTOTP();
        const setupUri = totpSetupDetails.getSetupUri(appName, userEmail);
        setQRCodeValue(setupUri.toString());
      } catch (err) {
        console.error(err);
      }
    }, [userStore]);

    const checkMFA = useCallback(async () => {
      // Await for the account store to load
      userStore.addFunctionToLoadCallback(() => {
        const mfaEnabled = userStore.mfaEnabled;
        const mfaRequired = userStore.mfaShouldBeEnabled;
        if (mfaRequired && !mfaEnabled) {
          setNeedToSetTOTP(true);
          setupTOTP();
        }
      });
    }, [userStore, setupTOTP]);

    const verifyTOTP = async (token: string) => {
      try {
        // const user = await Auth.currentAuthenticatedUser();
        await verifyTOTPSetup({ code: token });

        // Increment confirmation count
        setConfirmationCount((prevCount) => prevCount + 1);
        // // If confirmed twice, set user's preferred MFA method to TOTP
        if (confirmationCount + 1 >= 2) {
          await updateMFAPreference({ totp: "PREFERRED" });
          setNeedToSetTOTP(false);
          setQRCodeValue(null);
          toast.success("MFA auth enabled!");
        } else {
          toast.success(
            "First confirmation successful! Please confirm once more."
          );
        }
      } catch (_err: any) {
        const err = _err.toString();
        console.error(err);
        if (err.includes("InvalidParameterException")) {
          toast.error("Invalid token. The maximum lenght is 6.");
        } else if (err.includes("Code mismatch")) {
          toast.error("Invalid token. Please try again.");
        } else if (err.includes("EnableSoftwareTokenMFAException")) {
          toast.error("Invalid token. This token has already been used once.");
        }
      }
    };

    // Effect Declaration
    useEffect(() => {
      checkMFA();
    }, [checkMFA]);

    useEffect(() => {
      if (needToSetTOTP) setupTOTP();
    }, [needToSetTOTP, setupTOTP]);

    // Pass the state and setters as props to the wrapped component
    if (!needToSetTOTP) {
      return <WrappedComponent {...props} />;
    } else {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <ToastContainer
            position="bottom-left"
            autoClose={2500}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <Paragraph>
            <Title level={4}>Authenticator based MFA</Title>
            <Text>
              Your account setting is set to require Authenticator based MFA.
              Please follow the steps below to setup MFA.
            </Text>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* <button onClick={setupTOTP}>Setup TOTP</button> */}
              {QRCodeValue ? (
                <div>
                  <QRCode value={QRCodeValue} size={256} />
                  <input
                    type="text"
                    id="totpToken"
                    placeholder="Enter TOTP token"
                  />
                  <BlingsButton
                    onClick={() => {
                      const token = (
                        document.getElementById("totpToken") as HTMLInputElement
                      ).value;
                      verifyTOTP(token);
                    }}
                  >
                    Confirm TOTP Token
                  </BlingsButton>
                </div>
              ) : (
                <Skeleton.Image active={QRCodeValue !== undefined} />
              )}
            </div>
          </Paragraph>
        </div>
      );
    }
  };
};

export default withSetupTOTPMFA;
